import { Box, Card, Typography } from "@mui/material";
import { Talent } from "../../features/talents/talentsTypes";
import ArrowForwardIosRoundedIcon from "@mui/icons-material/ArrowForwardIosRounded";
import CustomRadarChart from "../shared/RadarChart";
import { useMemo } from "react";
import { buildRadarData } from "src/features/talents/matchingUtils";
import { useTranslation } from "react-i18next";
import TalentAvatar from "./TalentAvatar";
import {
  getMaskedTalentName,
  getNameInitial,
  useShouldMaskTalentIdentity,
} from "src/utils/talentPrivacy";

interface TalentCardProps {
  talent: Talent;
  onClick: () => void;
  active: boolean;
}

interface LeftBoxProps {
  status: string;
  labels: {
    rejected: string;
    requested: string;
    accepted: string;
  };
}

const LeftBox = ({ status, labels }: LeftBoxProps) => {
  const getStatusContent = (status: string) => {
    switch (status) {
      case "rejected":
        return (
          <Box
            sx={{
              ...sxProps.leftBox,
              bgcolor: "error.dark",
              border: "2px solid",
              borderColor: "error.dark",
              color: "error.contrastText",
            }}
          >
            {labels.rejected}
          </Box>
        );
      case "requested":
        return (
          <Box
            sx={{
              ...sxProps.leftBox,
              bgcolor: "warning.lighter",
              border: "2px solid",
              borderColor: "warning.dark",
              color: "warning.dark",
            }}
          >
            {labels.requested}
          </Box>
        );
      case "accepted":
        return (
          <Box
            sx={{
              ...sxProps.leftBox,
              bgcolor: "secondary.dark",
              border: "2px solid",
              borderColor: "secondary.dark",
              color: "secondary.contrastText",
            }}
          >
            {labels.accepted}
          </Box>
        );
      default:
        return (
          <Box
            sx={{
              ...sxProps.leftBox,
            }}
          ></Box>
        );
    }
  };

  return getStatusContent(status);
};

const TalentCard = ({ talent, onClick, active }: TalentCardProps) => {
  const { t } = useTranslation();
  const shouldMaskTalentIdentity = useShouldMaskTalentIdentity();
  const { avatarId, firstName, lastName, status, headline, completeness, id } =
    talent;
  const statusLabels = {
    rejected: t("filterBar.rejected"),
    requested: t("filterBar.requested"),
    accepted: t("filterBar.accepted"),
  };

  const point = Math.round(talent.match?.overallScore ?? Number(completeness) ?? 0);
  const data = useMemo(
    () => buildRadarData(talent.match?.groupScores || []),
    [talent.match?.groupScores]
  );
  const displayName = shouldMaskTalentIdentity
    ? getMaskedTalentName(firstName, lastName)
    : `${firstName} ${lastName}`.trim();
  const avatarFallback = shouldMaskTalentIdentity
    ? getMaskedTalentName(firstName, lastName)
    : firstName?.charAt(0);

  return (
    <Card
      sx={{
        minWidth: 320,
        width: "100%",
        maxWidth: 350,
        my: 1,
        display: "flex",
        alignItems: "center",
        pl: "30px",
        height: "100px",
        justifyContent: "space-between",
        position: "relative",
        cursor: "pointer",
        pr: active ? 0 : "30px",
      }}
      onClick={onClick}
    >
      <LeftBox status={status} labels={statusLabels} />
      <Box sx={{ display: "flex", alignItems: "center" }}>
        <TalentAvatar
          profileId={id}
          avatarId={avatarId}
          fallbackText={avatarFallback || getNameInitial(firstName)}
          blurred={shouldMaskTalentIdentity}
          sx={{ mr: 1 }}
        />
        <Box sx={{ width: 130 }}>
          <Typography variant="h4" fontSize={"1rem"}>
            {displayName}
          </Typography>
          <Typography variant="subtitle2" fontSize={12} color="textSecondary">
            {headline}
          </Typography>
        </Box>
      </Box>
      <CustomRadarChart
        name={displayName}
        data={data}
        point={String(point)}
        isCardView
      />
      {active && (
        <Box sx={sxProps.rightBox}>
          <ArrowForwardIosRoundedIcon sx={{ color: "white" }} />
        </Box>
      )}
    </Card>
  );
};

export default TalentCard;

const sxProps = {
  leftBox: {
    transform: "rotate(-90deg)",
    color: "white",
    width: "100px",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    height: "25px",
    position: "absolute",
    left: "-37.5px",
    mr: "33px",
    borderRadius: "8px 8px 0px 0px",
  },
  rightBox: {
    bgcolor: "primary.main",
    height: "100%",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    width: "25px",
  },
};
